import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import DownloadIcon from 'assets/icons/circles-design/download_icon.png'
import PlusIcon from 'assets/icons/circles-design/plus.png'
import PropTypes from 'prop-types'

import OrderAPI from '../../../../../../../api/orbitV1/OrderAPI'
import { OrderChangeStateActions } from '../../../../../../../types/order.type'
// copied code from UploadCsvBase.jsx
// should come up with an abstract component

/** Helper function to parse CSV file. */
function parseCsv(csvFile) {
  const csvFileRows = csvFile.split('\n')
  /** @type {import('../../../../../../../types/order.type').ProvisionPayload} */
  const values = []
  for (let i = 1; i < csvFileRows.length; i++) {
    const row = csvFileRows[i].split(',')
    values.push({
      iccid: row[0].replaceAll('"', '').trim(),
      msisdn: row[1].replaceAll('"', '').trim(),
      serviceInstanceId: row[2].replaceAll('"', '').trim()
    })
  }
  return values
}

/** Styled div that bolds content. */
const StrongText = styled.div({
  display: 'block',
  fontWeight: 'bold',
  fontSize: 16
})

/** Dotted box used for uploads */
function DottedBox(props) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #B8C4D3',
        borderRadius: '8px',
        width: '20%',
        padding: '30px 35px 30px 30px'
      }}
    >
      {props.children}
    </Box>
  )
}
DottedBox.propTypes = {
  children: PropTypes.element
}

const stepIconSx = {
  '.MuiStepIcon-root.Mui-active': {
    fill: 'blue',
    fontWeight: 'bold'
  }
}

const MANUAL_PROVISION_CSV_TEMPLATE_NAME = 'manual-provision-template.csv'
const MANUAL_PROVISION_CSV_TEMPLATE_DATA = [
  {
    iccid: '896572945007008368',
    msisdn: '87651234',
    serviceInstanceId: 'LW102157229'
  },
  {
    iccid: '896572940387491679',
    msisdn: '89123456',
    serviceInstanceId: 'LW102157330'
  }
]
const MANUAL_PROVISION_TEMPLATE_HEADERS = [
  { label: 'iccid', key: 'iccid' },
  { label: 'msisdn', key: 'msisdn' },
  { label: 'serviceInstanceId', key: 'serviceInstanceId' }
]

function UploadSims(props) {
  const { orderNo } = props
  const [uploadList, setUploadList] = useState([])
  const [uploadedFilename, setUploadedFilename] = useState(null)
  const navigate = useNavigate()

  const updateStateAndRedirect = async () => {
    try {
      const updateOrderStateResponse = await OrderAPI.updateOrderState(
        orderNo,
        {
          action: OrderChangeStateActions.OPS_MANUAL_PROVISION,
          metadata: {
            manualProvision: uploadList
          }
        }
      )

      if (updateOrderStateResponse.data) {
        toast.success('Order successfully updated')
        navigate(`/hq/manage-orders/${props.orderNo}/`, { replace: true })
      }
    } catch (error) {
      toast.error('Failed to update order')
    }
  }

  const onFileUpload = (file) => {
    const fileName = file.name
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target.result
      const parsedCsv = parseCsv(text)
      setUploadList(parsedCsv)
    }

    reader.readAsText(file)
    setUploadedFilename(fileName)
  }

  const TemplateBox = () => (
    <CSVLink
      data={MANUAL_PROVISION_CSV_TEMPLATE_DATA}
      filename={MANUAL_PROVISION_CSV_TEMPLATE_NAME}
      headers={MANUAL_PROVISION_TEMPLATE_HEADERS}
      style={{ textDecoration: 'none', cursor: 'pointer', display: 'flex' }}
    >
      <Typography
        sx={{
          color: '#0000FF',
          fontSize: '16px',
          fontWeight: 'bold',
          marginRight: '2px'
        }}
      >
        Download Template
      </Typography>
      <img
        src={DownloadIcon}
        alt='download-template-logo'
        width='20px'
        height='20px'
      />
    </CSVLink>
  )

  const UploadBoxEmpty = () => (
    <>
      <DottedBox>
        <>
          <label htmlFor='upload-man-prov' style={{ cursor: 'pointer' }}>
            <Box display='flex' alignItems='center' justifyContent='center'>
              <Typography
                sx={{
                  color: '#0000FF',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginRight: '4px'
                }}
              >
                Select File
              </Typography>
              <img
                src={PlusIcon}
                alt='upload-csv-logo'
                width='20px'
                height='20px'
              />
            </Box>
          </label>
          <input
            id='upload-man-prov'
            type='file'
            accept='.csv'
            onChange={(e) => onFileUpload(e.target.files[0])}
            hidden
          />
        </>
      </DottedBox>
      <Typography variant='subtitle1' sx={{ color: '#B8C4D3' }}>
        xlsx / csv, max 8mb
      </Typography>
      {uploadedFilename && (
        <Typography>
          Uploaded file: <strong>{uploadedFilename}</strong>
        </Typography>
      )}
    </>
  )

  return (
    <Stepper orientation='vertical' sx={stepIconSx}>
      <Step active>
        <StepLabel>
          <StrongText>
            Download and follow this template to upload a list of items for this
            order.
          </StrongText>
        </StepLabel>
        <StepContent>
          <TemplateBox />
        </StepContent>
      </Step>
      <Step active>
        <StepLabel>
          <StrongText>Save and upload the file below.</StrongText>
        </StepLabel>
        <StepContent>
          <UploadBoxEmpty />
        </StepContent>
      </Step>
      <Step active={Boolean(uploadedFilename)}>
        <StepLabel>
          <StrongText>Upload File</StrongText>
        </StepLabel>
        <StepContent>
          <Button variant='filled' onClick={updateStateAndRedirect}>
            Confirm upload file: <br />
            {uploadedFilename}
          </Button>
        </StepContent>
      </Step>
    </Stepper>
  )
}

UploadSims.propTypes = {
  orderNo: PropTypes.string
}

export default UploadSims
